// Debounced-commit local draft for the list's search box (docs/06-ux-rules.md
// "List" search row; 11-chassis-plan.md §2 PR 04: "debounced 300ms → q,
// cancel pending on unmount and when the controlling param changes"). Kept
// as its own hook (rather than inline in `SearchHeader`) so the debounce/
// cancel semantics get a dedicated, branching-logic test per the repo's
// hook-testing convention.
import { useCallback, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { SEARCH_DEBOUNCE_MS } from '../constants'

export interface UseDebouncedSearchResult {
  // The input's live value — updates synchronously on every keystroke, even
  // though the commit to `onCommit` is debounced.
  draft: string
  onDraftChange: (value: string) => void
  // Clears immediately (no debounce) — used by the clear button.
  clear: () => void
}

export function useDebouncedSearch(
  value: string,
  onCommit: (value: string) => void
): UseDebouncedSearchResult {
  const [draft, setDraft] = useState(value)
  const [debouncedCommit, cancel] = useDebouncedCallback(
    onCommit,
    SEARCH_DEBOUNCE_MS
  )

  // The controlling param (`value`, sourced from the URL) changed — from
  // outside this component (browser Back, a filter reset, another tab of
  // the same list). Re-sync the draft and drop any pending debounced commit
  // so it can't later fire and stomp the value that just changed underneath
  // it.
  useEffect(() => {
    setDraft(value)
    cancel()
    // `cancel` is stable for the lifetime of a given `onCommit`/delay pair
    // (use-debounce memoizes it), so omitting it here doesn't risk a stale
    // closure — only `value` should re-run this sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Also cancel on unmount (docs/11-chassis-plan.md PR 04 spec, explicit
  // beyond what the `value`-change effect above already covers — e.g.
  // navigating away from the inbox entirely mid-debounce).
  useEffect(() => cancel, [cancel])

  const onDraftChange = useCallback(
    (next: string) => {
      setDraft(next)
      debouncedCommit(next)
    },
    [debouncedCommit]
  )

  const clear = useCallback(() => {
    setDraft('')
    cancel()
    onCommit('')
  }, [cancel, onCommit])

  return { draft, onDraftChange, clear }
}
